import { generateUniqueKeySeparator } from "@common/utils";
import Sequence from "../Sequence";
import { ChConfig } from "../models/ChConfig";
import define from "../define";
import BootOptionModel from "./BootOption";
import { Media } from "./Media";
import { Post } from "./Post";
import { ChDetail } from "./ChDetail";

export type Ch = {
  tuneId: string;
  connection: string;
  connections: string[];
  type: string;
  liveCnt: number;
  liveRevision?: number;
  favicon: string;
  gateway: string;
  server: string;
  active: boolean;
  audios: Media[];
  videos: Media[];
  latestPost?: Post;
  meta?: ChDetail;
};

export const init: Ch = {
  tuneId: "",
  connection: "",
  connections: [],
  type: "",
  liveCnt: 0,
  liveRevision: 0,
  favicon: "",
  gateway: "",
  server: "",
  active: false,
  audios: [],
  videos: [],
};

export type GetChPropsParams = {
  tuneId: string;
  host: string;
  connection: Connection;
  liveCnt: number;
  liveRevision?: number;
  chConfig: ChConfig | null;
};

export type GetConnectionsOptions = {
  isSelfExclude?: boolean;
  isSortUpperLayer?: boolean;
};

export const getConnectionsOptions = {
  isSelfExclude: false,
  isSortUpperLayer: true,
};

export type ParentConnection = string | undefined;
export type TuneConnection = string;
export type ChildConnection = string;
export type Connection = string;

export default class ChModel {
  static separetor = "/";
  static rootConnection = ChModel.separetor;
  static connectionSeparator = "/";
  static defaultProtocol = "talkn::";
  static defultType = "text/html";
  static plainType = "plain";
  constructor(params: Partial<Ch> = init) {
    return Object.assign(this, params);
  }
  static getParentConnection(fixConnection: string): ParentConnection {
    if (fixConnection === ChModel.rootConnection) return undefined;
    let replacedConnection = fixConnection.replace(/.$/, "");
    const lastSlashIndex = replacedConnection.lastIndexOf("/");
    return replacedConnection.substring(0, lastSlashIndex + 1);
  }
  static getTopConnection(connection: string): Connection {
    if (connection === ChModel.rootConnection) return ChModel.rootConnection;
    const sep = this.separetor;
    return `${sep}${connection.split(sep)[1]}${sep}` as Connection;
  }
  static getConnection(connection: string) {
    return BootOptionModel.getConnection(connection);
  }
  static getConnectionFromUrl(url: string): string {
    const queryString = url.split("?")[1];
    const params = new URLSearchParams(queryString);
    const rawTuneId = params.get("tuneId");
    if (rawTuneId === null) {
      return ChModel.rootConnection;
    } else {
      return ChModel.getConnectionFromTuneId(rawTuneId);
    }
  }
  static getConnectionFromTuneId(tuneId: string): string {
    const decoded = decodeURIComponent(tuneId ?? "");
    const escapedSeparator = generateUniqueKeySeparator.replace(
      /[-\/\\^$*+?.()|[\]{}]/g,
      "\\$&"
    );
    const pattern = new RegExp(
      `^con${escapedSeparator}(.*?)${escapedSeparator}`
    );
    const match = decoded.match(pattern);
    let connection = match?.[1] ? match?.[1] : ChModel.rootConnection;
    connection = connection.startsWith(ChModel.rootConnection)
      ? connection
      : `${ChModel.rootConnection}${connection}`;
    connection = connection.endsWith(ChModel.rootConnection)
      ? connection
      : `${connection}${ChModel.rootConnection}`;
    return connection;
  }
  static getFavicon(host: string) {
    if (host === define.DEVELOPMENT_DOMAIN) return "";
    if (host === ChModel.rootConnection) return "";
    return `${host}/favicon.ico`;
  }
  static getConnections(
    connection: Connection,
    options: GetConnectionsOptions = getConnectionsOptions
  ) {
    const { isSelfExclude, isSortUpperLayer } = options;
    let connections = [ChModel.rootConnection];
    if (connection && connection !== ChModel.rootConnection) {
      const connectionArr = connection
        .split(ChModel.connectionSeparator)
        .filter((part) => part !== "");
      let connectionPart = "";

      connectionArr.forEach((segment) => {
        connectionPart += `${this.separetor}${segment}`;
        const addConnection = `${connectionPart}${this.separetor}`;
        if (!(isSelfExclude && addConnection === connection)) {
          connections.push(addConnection);
        }
      });
    }

    if (isSortUpperLayer) {
      connections.sort((a, b) => a.length - b.length);
    } else {
      connections.sort((a, b) => b.length - a.length);
    }

    return connections;
  }

  /**
   * connections配列から指定範囲の階層を抽出する
   *
   * @param connections - 階層リスト（例: ['/', '/a/', '/a/b/']）
   * @param startConnection - 開始階層（この階層は含まれる）
   * @param endConnection - 終了階層（この階層は含まれない）
   * @returns startConnectionからendConnectionまでの間に存在する階層配列
   *
   * @example
   * getMyConnectionClass(['/', '/a/', '/a/b/'], '/', '/a/b/')
   * // => ['/', '/a/'] (終了階層の /a/b/ は含まれない)
   *
   * @example
   * getMyConnectionClass(['/', '/a/'], '/', '/a/')
   * // => ['/'] (開始階層は含む、終了階層は含まない)
   *
   * @example
   * getMyConnectionClass(['/', '/a/'], '/a/', '/a/')
   * // => [] (start === end の場合は空配列)
   */
  static getMyConnections(
    connections: Connection[],
    startConnection: Connection,
    endConnection?: Connection
  ): Connection[] {
    const myConnectionClass: Connection[] = [];

    const loopConnections = connections
      .slice()
      .sort((a, b) => a.length - b.length);
    endConnection = endConnection
      ? endConnection
      : loopConnections[loopConnections.length - 1];

    let isPush = false;
    for (const i in loopConnections) {
      if (loopConnections[i] === startConnection) {
        isPush = true;
      }
      if (loopConnections[i] === endConnection) break;
      if (isPush) {
        myConnectionClass.push(loopConnections[i]);
      }
    }
    return myConnectionClass;
  }

  static getType(host: string) {
    return host.startsWith(Sequence.HTTPS_PROTOCOL) ||
      host.startsWith(Sequence.HTTP_PROTOCOL)
      ? ChModel.defultType
      : ChModel.plainType;
  }

  static isMediaCh(type: string) {
    return type?.startsWith("audio") || type?.startsWith("video");
  }

  static getGateway(chConfig: ChConfig | null) {
    return chConfig && chConfig.gateway?.host && chConfig.gateway.port
      ? `${chConfig.gateway.host}:${chConfig.gateway.port}`
      : `127.0.0.1:${define.PORTS.IO_ROOT}`;
  }

  static getServer(chConfig: ChConfig | null) {
    return chConfig && chConfig.ioServer.host && chConfig.ioServer.port
      ? `${chConfig.ioServer.host}:${chConfig.ioServer.port}`
      : `127.0.0.1:${define.PORTS.IO_ROOT}`;
  }

  static getChParams = (params: GetChPropsParams): Partial<Ch> => {
    const { tuneId, connection: _connection, host, liveCnt, liveRevision, chConfig } = params;
    const connection = ChModel.getConnection(_connection);
    const connections = ChModel.getConnections(connection);
    const favicon = ChModel.getFavicon(host);
    const type = ChModel.getType(host);
    const gateway = ChModel.getGateway(chConfig);
    const server = ChModel.getServer(chConfig);
    return {
      tuneId,
      connection,
      connections,
      favicon,
      type,
      liveCnt,
      liveRevision,
      gateway,
      server,
    };
  };
}
